import {
  cancelEnvelope,
  sendReminder,
  type SignatureEnvelope,
  type SignatureEnvelopeStatus,
  type SignatureRecipient,
} from '@commenda/api/docusign';
import { Badge, Button, useToggle, showNotification } from '@commenda/kakashi';
import { downloadFileFromFileId } from '@commenda/utils/downloadFileFromLink';
import { ESignatureHelper, ESignTaskStatus } from '@commenda/utils/models';
import {
  SignatureRecipientStatus,
  TaskParty,
} from '@prisma/client';
import { useState } from 'react';
import { CancelEnvelopeConfirmationModal } from './CancelEnvelopeConfirmationModal';

type SignatureTaskCardProps = {
  recipient: SignatureRecipient;
  documentName: string;
  envelopeId: string;
  envelopeStatus: SignatureEnvelopeStatus;
  documents: SignatureEnvelope['Documents'];
  role: TaskParty;
  /** All recipients on this envelope (for cancel confirmation blast radius). */
  allRecipients: SignatureRecipient[];
  voidedReason?: string | null;
  declinedByName?: string | null;
  onReminderSent?: () => void;
  onEnvelopeCancelled?: () => void;
};

export const SignatureTaskCard = ({
  recipient,
  documentName,
  envelopeId,
  envelopeStatus,
  documents,
  role,
  allRecipients,
  voidedReason,
  declinedByName,
  onReminderSent,
  onEnvelopeCancelled,
}: SignatureTaskCardProps) => {
  const reminderLoading = useToggle(false);
  const cancelling = useToggle(false);
  const downloading = useToggle(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const reminderSentToday = useToggle(false);

  const taskStatus: ESignTaskStatus = ESignatureHelper.getTaskStatus(
    recipient.status,
    envelopeStatus
  );
  const statusConfig = ESignatureHelper.TASK_STATUS_DISPLAY[taskStatus];
  const header = ESignatureHelper.getTaskHeader(taskStatus, documentName);
  const hasAnotherSignerActive = allRecipients.some(
    (r) =>
      r.id !== recipient.id &&
      (r.status === SignatureRecipientStatus.SENT ||
        r.status === SignatureRecipientStatus.DELIVERED)
  );
  const description = ESignatureHelper.getTaskDescription({
    taskStatus,
    recipientName: recipient.recipientName,
    recipientEmail: recipient.recipientEmail,
    signedAt: recipient.signedAt ?? undefined,
    declinedByName: declinedByName ?? undefined,
    hasMultipleSigners: allRecipients.length > 1,
    hasAnotherSignerActive,
  });

  const signedDoc =
    recipient.status === 'SIGNED'
      ? ESignatureHelper.findSignedDocument(documents)
      : undefined;

  const canSendReminder =
    ESignatureHelper.canSendReminder(recipient.status, envelopeStatus) &&
    !reminderSentToday.value;
  const canCancelEnvelope =
    role === TaskParty.AGENT &&
    ESignatureHelper.canCancelEnvelope(envelopeStatus) &&
    recipient.status !== 'SIGNED';
  const showActions = taskStatus !== ESignTaskStatus.Canceled;

  const handleSendReminder = async () => {
    reminderLoading.on();

    try {
      const res = await sendReminder(recipient.id);
      if (!res.ok) {
        if (res.status === 429) {
          reminderSentToday.on();
          showNotification({
            title: 'Reminder already sent today',
            valid: false,
          });
        } else {
          showNotification({
            title: 'Failed to send reminder',
            message: ESignatureHelper.GENERIC_ERROR,
            valid: false,
          });
        }
        return;
      }
      reminderSentToday.on();
      onReminderSent?.();
      showNotification({
        title: 'Reminder sent',
        valid: true,
      });
    } catch {
      showNotification({
        title: 'Failed to send reminder',
        message: ESignatureHelper.GENERIC_ERROR,
        valid: false,
      });
    } finally {
      reminderLoading.off();
    }
  };

  const handleCancelEnvelope = async () => {
    cancelling.on();
    try {
      const res = await cancelEnvelope(envelopeId);
      if (!res.ok) {
        showNotification({
          title: 'Failed to cancel envelope',
          message: ESignatureHelper.GENERIC_ERROR,
          valid: false,
        });
        return;
      }
      onEnvelopeCancelled?.();
    } catch {
      showNotification({
        title: 'Failed to cancel envelope',
        message: ESignatureHelper.GENERIC_ERROR,
        valid: false,
      });
    } finally {
      cancelling.off();
    }
  };

  const handleDownloadSigned = async () => {
    if (!signedDoc) return;
    downloading.on();
    try {
      await downloadFileFromFileId(signedDoc.id, signedDoc.fileName);
    } catch {
      showNotification({
        title: 'Failed to download signed document',
        valid: false,
      });
    } finally {
      downloading.off();
    }
  };

  const allRecipientNames = allRecipients.map(
    (r) => r.recipientName || r.recipientEmail
  );

  return (
    <>
      <div className="flex gap-[10px] items-start w-full p-4">
        <div className="flex-1 flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="txt-small-medium text-text-primary truncate">
              {role === TaskParty.CLIENT
                ? ESignatureHelper.getClientTaskHeader(
                    taskStatus,
                    recipient.recipientName
                  )
                : header}
            </span>
            <Badge size="sm" variant={statusConfig.variant}>
              {statusConfig.label}
            </Badge>
          </div>
          <p className="txt-small-regular text-text-secondary truncate">
            {description}
          </p>
        </div>

        {showActions && (
          <div className="flex items-center gap-2 shrink-0">
            {signedDoc && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownloadSigned}
                disabled={downloading.value}
              >
                {downloading.value ? 'Downloading...' : 'Download'}
              </Button>
            )}
            {canSendReminder && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSendReminder}
                disabled={reminderLoading.value}
              >
                {reminderLoading.value ? 'Sending...' : 'Send Reminder'}
              </Button>
            )}
            {canCancelEnvelope && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCancelModalOpen(true)}
                disabled={cancelling.value}
              >
                {cancelling.value ? 'Cancelling...' : 'Cancel'}
              </Button>
            )}
          </div>
        )}
      </div>

      <CancelEnvelopeConfirmationModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        documentName={documentName}
        recipientName={recipient.recipientName || recipient.recipientEmail}
        allRecipientNames={allRecipientNames}
        onConfirm={handleCancelEnvelope}
        isCancelling={cancelling.value}
      />
    </>
  );
};
